import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orgDashboardTemplatesService } from '../services/dashboards.service';

// ==================== Query Keys ====================

export const orgTemplateKeys = {
  all: ['org-dashboard-templates'] as const,
  template: (roleType: string) => [...orgTemplateKeys.all, 'template', roleType] as const,
  catalog: (roleType: string) => [...orgTemplateKeys.all, 'catalog', roleType] as const,
  myDashboard: () => [...orgTemplateKeys.all, 'my-dashboard'] as const,
};

// ==================== Read Hooks ====================

export function useOrgTemplate(roleType: string) {
  return useQuery({
    queryKey: orgTemplateKeys.template(roleType),
    queryFn: () => orgDashboardTemplatesService.getTemplate(roleType),
    enabled: !!roleType,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useOrgCatalog(roleType: string) {
  return useQuery({
    queryKey: orgTemplateKeys.catalog(roleType),
    queryFn: () => orgDashboardTemplatesService.getCatalog(roleType),
    enabled: !!roleType,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMyDashboard() {
  return useQuery({
    queryKey: orgTemplateKeys.myDashboard(),
    queryFn: () => orgDashboardTemplatesService.getMyDashboard(),
    staleTime: 5 * 60 * 1000, // 5 min -- prevent refetch on window focus from rearranging layout
    refetchOnWindowFocus: false, // layout should not change when user switches tabs
  });
}

// ==================== Mutation Hooks ====================

export function useUpdateOrgTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleType, ...dto }: {
      roleType: string;
      layout?: any[];
      settings?: Record<string, any>;
    }) => orgDashboardTemplatesService.updateTemplate(roleType, dto),
    onSuccess: (data) => {
      queryClient.setQueryData(orgTemplateKeys.template(data.roleType), data);
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.myDashboard() });
    },
  });
}

export function useAddOrgWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleType, ...dto }: {
      roleType: string;
      widgetType: string;
      title: string;
      config?: Record<string, any>;
      position?: { x: number; y: number; w: number; h: number };
    }) => orgDashboardTemplatesService.addWidget(roleType, dto),
    onSuccess: (_, { roleType }) => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.template(roleType) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.myDashboard() });
    },
  });
}

export function useUpdateOrgWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleType, widgetId, ...dto }: {
      roleType: string;
      widgetId: string;
      title?: string;
      config?: Record<string, any>;
      position?: { x: number; y: number; w: number; h: number };
      isEnabled?: boolean;
    }) => orgDashboardTemplatesService.updateWidget(roleType, widgetId, dto),
    onSuccess: (_, { roleType }) => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.template(roleType) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.myDashboard() });
    },
  });
}

export function useRemoveOrgWidget() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ roleType, widgetId }: { roleType: string; widgetId: string }) =>
      orgDashboardTemplatesService.removeWidget(roleType, widgetId),
    onSuccess: (_, { roleType }) => {
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.template(roleType) });
      queryClient.invalidateQueries({ queryKey: orgTemplateKeys.myDashboard() });
    },
  });
}
